import { useEffect } from 'react';
import { AlertCircle, Check, Loader2, Film, ImageIcon, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  usePresets,
  type ContentPreset,
  type PresetCategory,
} from '@/hooks/usePresets';
import type { ContentFormData } from '..';

interface PresetSelectProps {
  formData: ContentFormData;
  updateFormData: (data: Partial<ContentFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

const CATEGORY_ORDER: PresetCategory[] = ['video', 'image', 'motion'];
const CATEGORY_META: Record<PresetCategory, { label: string; icon: typeof Film }> = {
  video: { label: 'Video', icon: Film },
  image: { label: 'Image', icon: ImageIcon },
  motion: { label: 'Motion graphics', icon: Type },
};

export function PresetSelect({ formData, updateFormData, onValidationChange }: PresetSelectProps) {
  const { data, isLoading, isError, refetch } = usePresets();
  const presets = data?.presets || [];
  const currentPreset = formData.presetId || 'none';
  const activePreset = presets.find(preset => preset.id === currentPreset);

  useEffect(() => {
    if (!data) return;
    const supported =
      currentPreset === 'none' || presets.some(preset => preset.id === currentPreset);
    if (!supported) updateFormData({ presetId: 'none' });
    onValidationChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currentPreset, onValidationChange]);

  const selectPreset = (preset: ContentPreset) => {
    // Derive a scene count from the preset's target average; the backend
    // distributes the total into varied per-scene durations from there.
    const avg = preset.target_avg_scene_secs || 10;
    const sceneCount = Math.min(20, Math.max(1, Math.round(formData.duration / avg)));
    updateFormData({
      presetId: preset.id,
      sceneLength: avg,
      sceneCount,
      duration: sceneCount * avg,
      // Opinionated prefills — all still changeable in the later steps
      ...(preset.default_visual_model ? { visualModel: preset.default_visual_model } : {}),
      ...(preset.default_visual_style ? { recraftStyle: preset.default_visual_style } : {}),
      ...(preset.default_voice ? { voice: preset.default_voice } : {}),
      ...(preset.default_style ? { style: preset.default_style } : {}),
      ...(preset.default_direction ? { styleNotes: preset.default_direction } : {}),
    });
    onValidationChange(true);
  };

  const skipPreset = () => {
    // Skip means a clean slate — undo anything a previously-clicked preset
    // prefilled into the shared form fields.
    updateFormData({
      presetId: 'none',
      visualModel: 'auto',
      recraftStyle: undefined,
      voice: '',
      style: 'An even mix of clear information and engaging delivery.',
      styleNotes: '',
      sceneLength: 10,
      duration: (formData.sceneCount || 3) * 10,
    });
    onValidationChange(true);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading presets...
      </div>
    );
  }

  if (isError || presets.length === 0) {
    return (
      <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-muted-foreground">Presets could not be loaded.</p>
        <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          A preset commits to a whole look — generator, narration voice, pacing, and
          direction. Optional: skip it and direct every choice yourself.
        </p>
        <button
          type="button"
          onClick={skipPreset}
          className={cn(
            'shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            currentPreset === 'none'
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-border text-muted-foreground hover:border-primary/40',
          )}
        >
          {currentPreset === 'none' ? 'No preset' : 'Skip — no preset'}
        </button>
      </div>

      {/* Hero preview — swaps with the selected preset */}
      <div className="relative aspect-video w-full overflow-hidden rounded-xl border bg-black">
        {activePreset?.preview_url ? (
          activePreset.preview_type === 'image' ? (
            <img
              key={activePreset.preview_url}
              src={activePreset.preview_url}
              className="h-full w-full object-cover"
              alt={activePreset.name}
            />
          ) : (
            <video
              key={activePreset.preview_url}
              src={activePreset.preview_url}
              className="h-full w-full object-cover"
              muted
              autoPlay
              loop
              playsInline
            />
          )
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted/20">
            <p className="max-w-xs text-center text-sm text-muted-foreground">
              No preset — you'll direct the look, voice, and pacing yourself in the
              next steps. Or pick one below for a strong starting point.
            </p>
          </div>
        )}

        {activePreset && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 pt-12">
            <span className="text-base font-semibold text-white">{activePreset.name}</span>
            <p className="mt-0.5 text-xs text-white/80">{activePreset.description}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {activePreset.effect_chips.map(chip => (
                <span
                  key={chip}
                  className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm"
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Preset picker, grouped by output type — clicking swaps the hero */}
      {CATEGORY_ORDER.map(category => {
        const group = presets.filter(preset => preset.category === category);
        if (group.length === 0) return null;
        const { label, icon: Icon } = CATEGORY_META[category];
        return (
          <div key={category} className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Icon className="h-3.5 w-3.5" />
              {label}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {group.map(preset => {
                const isActive = currentPreset === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => selectPreset(preset)}
                    className={cn(
                      'flex items-start justify-between gap-2 rounded-lg border-2 px-3 py-2.5 text-left transition-colors',
                      isActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{preset.name}</p>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {preset.tagline}
                      </p>
                    </div>
                    {isActive && (
                      <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary">
                        <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
